import { ReactNode } from "react"
import { Link } from "react-router-dom"
import { motion } from "motion/react"
import PostersBackground, { POSTERS_SECTION_MIN_H } from "../features/start/PostersBackground"
import { ForbiddenIllustration, NotFoundIllustration, ServerErrorIllustration } from "./error/ErrorIllustrations"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type StickerConfig = {
    label: string
    color: string
    rotate?: number
}

type ActionConfig = {
    label: string
    to?: string
    state?: unknown
    onClick?: () => void
    variant?: "primary" | "secondary"
}

export type ErrorPageProps = {
    code: string
    stickers: StickerConfig[]
    title: ReactNode
    description: string
    accentColor: string
    illustration: "not-found" | "forbidden" | "server-error"
    marquee?: string[]
    primaryAction: ActionConfig
    secondaryAction?: ActionConfig
}

function Sticker({ label, color, rotate = 0 }: StickerConfig) {
    return (
        <span
            className="inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-widest whitespace-nowrap select-none"
            style={{
                fontFamily: MONO,
                color,
                borderColor: `${color}66`,
                background: `${color}22`,
                transform: `rotate(${rotate}deg)`,
            }}>
            {label}
        </span>
    )
}

function ActionButton({ label, to, state, onClick, variant = "primary", accentColor }: ActionConfig & { accentColor: string }) {
    const isPrimary = variant === "primary"
    const className =
        "inline-flex w-full items-center justify-center rounded-full px-6 py-3.5 text-[13px] transition-colors sm:w-auto"
    const style = isPrimary
        ? {
              background: accentColor,
              fontFamily: MONO,
              fontWeight: 700 as const,
              color: "#fff",
              boxShadow: `0 0 28px ${accentColor}73`,
              letterSpacing: "0.04em",
          }
        : {
              fontFamily: MONO,
              fontWeight: 700 as const,
              letterSpacing: "0.04em",
          }

    const content = (
        <motion.span whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className={isPrimary ? className : `${className} border border-white/12 bg-white/[0.04] text-white/55 hover:border-white/20 hover:bg-white/[0.07] hover:text-white/75`} style={style}>
            {label}
        </motion.span>
    )

    if (to) {
        return (
            <Link to={to} state={state}>
                {content}
            </Link>
        )
    }

    return (
        <button type="button" onClick={onClick} className="border-0 bg-transparent p-0">
            {content}
        </button>
    )
}

const ILLUSTRATIONS = {
    "not-found": NotFoundIllustration,
    forbidden: ForbiddenIllustration,
    "server-error": ServerErrorIllustration,
} as const

function ErrorMarquee({ items, color }: { items: string[]; color: string }) {
    const track = [...items, ...items]

    return (
        <div
            className="overflow-hidden py-2.5"
            style={{
                background: `${color}14`,
                borderTop: `1px solid ${color}30`,
                borderBottom: `1px solid ${color}30`,
            }}>
            <div className="error-marquee-track flex w-max">
                {[0, 1].map(copy => (
                    <div key={copy} className="flex shrink-0 items-center" aria-hidden={copy === 1}>
                        {track.map((item, i) => (
                            <span
                                key={`${copy}-${i}`}
                                className="inline-flex shrink-0 items-center gap-2 px-5 text-[11px] font-bold uppercase tracking-[0.1em]"
                                style={{ fontFamily: MONO, color }}>
                                <span aria-hidden>✦</span>
                                {item}
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function ErrorPage({
    code,
    stickers,
    title,
    description,
    accentColor,
    illustration,
    marquee,
    primaryAction,
    secondaryAction,
}: ErrorPageProps) {
    const Illustration = ILLUSTRATIONS[illustration]

    return (
        <section
            className={`error-page-breakout relative w-screen overflow-hidden pb-20 md:pb-8 ${POSTERS_SECTION_MIN_H}`}
            style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}>
            <PostersBackground mobileAlign="center" />

            <div
                className="pointer-events-none absolute inset-0 z-[1]"
                style={{
                    background:
                        "linear-gradient(160deg, rgba(22,40,74,0.94) 0%, rgba(24,19,59,0.88) 45%, rgba(22,40,74,0.72) 100%)",
                }}
            />

            <div className="relative z-10 mx-auto flex w-full max-w-[1240px] flex-col items-center px-4 py-10 text-center sm:px-8 sm:py-14 lg:min-h-[560px] lg:justify-center">
                <div className="flex w-full max-w-[620px] flex-col items-center gap-7">
                    <div className="flex flex-wrap justify-center gap-2.5">
                        {stickers.map(sticker => (
                            <Sticker key={sticker.label} {...sticker} />
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.45 }}
                        className="relative">
                        <span
                            className="pointer-events-none absolute -inset-x-8 -top-6 select-none text-[clamp(88px,22vw,160px)] font-black leading-none opacity-[0.06]"
                            style={{ fontFamily: DISPLAY, color: accentColor }}>
                            {code}
                        </span>
                        <Illustration accent={accentColor} />
                    </motion.div>

                    <div className="flex flex-col items-center gap-3">
                        <motion.h1
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.06 }}
                            className="text-white leading-[1.05]"
                            style={{
                                fontFamily: DISPLAY,
                                fontWeight: 800,
                                fontSize: "clamp(28px, 6vw, 48px)",
                                letterSpacing: "-0.02em",
                            }}>
                            {title}
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.12 }}
                            className="max-w-md text-[15px] leading-relaxed sm:text-[16px]"
                            style={{ fontFamily: BODY, fontWeight: 500, color: "rgba(244,246,255,0.68)" }}>
                            {description}
                        </motion.p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: 0.18 }}
                        className="flex w-full max-w-[340px] flex-col items-center gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:justify-center">
                        <ActionButton {...primaryAction} accentColor={accentColor} variant="primary" />
                        {secondaryAction && (
                            <ActionButton {...secondaryAction} accentColor={accentColor} variant="secondary" />
                        )}
                    </motion.div>

                    <p
                        className="text-[11px] uppercase tracking-[0.22em] text-white/25"
                        style={{ fontFamily: MONO, fontWeight: 600 }}>
                        ошибка {code}
                    </p>
                </div>
            </div>

            {marquee && marquee.length > 0 && <ErrorMarquee items={marquee} color={accentColor} />}
        </section>
    )
}
